import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { AuthConfig } from '@/lib/types'
import { authService } from '@/lib/auth'
import { useToast } from '@/hooks/use-toast'
import { useTeams } from '@/contexts/TeamsContext'
import { handleTeamsSSO } from '@/lib/teams-auth'

interface LoginPageProps {
  authConfig: AuthConfig
}

export function LoginPage({ authConfig }: LoginPageProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { isInTeams, isInitialized } = useTeams()
  const isExpired = searchParams.get('expired') === 'true'

  // Auto sign-in when running inside Teams (no button click needed)
  useEffect(() => {
    if (!isInitialized || !isInTeams) return

    const signInWithTeams = async () => {
      setIsLoading(true)
      try {
        await handleTeamsSSO()
        navigate('/')
      } catch (error) {
        console.error('Teams SSO failed:', error)
        setIsLoading(false)
        const message = error instanceof Error ? error.message : String(error)
        toast({
          title: 'Teams sign-in failed',
          description: message,
          variant: 'destructive',
        })
      }
    }

    signInWithTeams()
  }, [isInitialized, isInTeams])

  useEffect(() => {
    if (isExpired) {
      toast({
        title: 'Session Expired',
        description: 'Your session has expired. Please sign in again.',
        variant: 'default',
      })
    }
  }, [isExpired, toast])

  const handleLogin = async () => {
    try {
      setIsLoading(true)
      await authService.login()
    } catch (error) {
      console.error('Login failed:', error)
      toast({
        title: 'Login Failed',
        description: 'Failed to authenticate. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-primary-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>

          <div>
            <h1 className="text-3xl font-bold">AI Internal Chat</h1>
            <p className="text-muted-foreground mt-2">
              {isInTeams
                ? 'Signing you in with your Teams account...'
                : isExpired
                ? 'Your session has expired. Please sign in again to continue.'
                : 'Sign in to access the internal AI chat system'}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Button
            onClick={isInTeams ? () => handleTeamsSSO().then(() => navigate('/')) : handleLogin}
            disabled={isLoading}
            className="w-full"
            size="lg"
          >
            {isLoading ? (
              <>
                <div className="spinner w-4 h-4 mr-2"></div>
                Signing in...
              </>
            ) : isInTeams ? (
              'Sign in with Teams'
            ) : (
              'Sign in with Microsoft'
            )}
          </Button>

          {!isInTeams && (
            <div className="text-center text-sm text-muted-foreground">
              <p>You will be redirected to Microsoft to sign in</p>
            </div>
          )}
        </div>

        <div className="text-center text-xs text-muted-foreground">
          <p>Powered by Azure OpenAI</p>
        </div>
      </div>
    </div>
  )
}
